import React, { PropsWithChildren } from 'react';
import ModuleRoute from 'holocron-module-route';
import { IndexRoute } from '@americanexpress/one-app-router';
import { Store } from 'redux';
import requireAuth from './utils/requireAuth';
import SystemError from './components/SystemError';
import AccessError from './components/AccessError';
import UnauthorizedError from './components/UnauthorizedError';
import getOneIdentityUrl from './selectors/getOneIdentityUrl';
import { ReduxState } from './types';
import NoProducts from './components/NoProductsError';
import { Dls6Wrapper, Dls7Wrapper } from './components/DlsWrapper';

const getHeaderProps = (state: ReduxState) => {
  const dashboardUrl = state.getIn(['config', 'dashboardUrl']) as string;
  const loginPage = getOneIdentityUrl(state);
  return {
    loginPage,
    loginDestPage: dashboardUrl,
    logoutDestPage: dashboardUrl,
  };
};

const getEnrollmentHeaderProps = (state: ReduxState) => {
  const origin = state.getIn(['browser', 'location', 'origin']) as string;
  const secureOrigin = origin.replace('http:', 'https:');
  const url = state.getIn(['modules', 'commercial-experience-root', 'url']) as string;
  const currentPage = `${secureOrigin}${url}`;
  const loginPage = getOneIdentityUrl(state);
  return {
    loginPage,
    loginDestPage: encodeURIComponent(currentPage),
    logoutDestPage: encodeURIComponent(currentPage),
  };
};

const getInvoiceManagementHeaderProps = (state: ReduxState) => {
  const invoiceManagementUrl = state.getIn(['config', 'invoiceManagementUrl']) as string;
  const loginPage = getOneIdentityUrl(state);
  return {
    loginPage,
    loginDestPage: invoiceManagementUrl,
    logoutDestPage: invoiceManagementUrl,
  };
};

export const DemoWrapper = ({ children }: PropsWithChildren) => <main>{children}</main>;

const childRoutes = (store: Store) => [
  // eslint-disable-next-line react/jsx-key -- these do not need keys
  <ModuleRoute path="/">
    <ModuleRoute
      moduleName="axp-page-wrapper"
      containerClassName=""
      headerProps={getHeaderProps(store.getState())}
    >
      <ModuleRoute path=":locale/business/payment-solutions">
        <ModuleRoute onEnter={requireAuth(store)} title="Authenticated Routes">
          <ModuleRoute path="supplier-dashboard">
            <ModuleRoute component={Dls7Wrapper}>
              <ModuleRoute moduleName="axp-supplier-dashboard-header">
                <ModuleRoute moduleName="axp-supplier-address-collection-wrapper">
                  <ModuleRoute
                    path="view-payment/:dataId/matched-invoice/:invoiceId"
                    moduleName="axp-supplier-matched-invoice-details"
                  />
                  <ModuleRoute
                    path="view-payment"
                    moduleName="axp-supplier-authenticated-virtual-payment-details"
                  />
                  <ModuleRoute
                    path="home"
                    moduleName="axp-supplier-authenticated-virtual-payment-dashboard"
                  />
                  <ModuleRoute
                    path="user-management"
                    moduleName="axp-supplier-authenticated-user-management"
                  />
                  <ModuleRoute path="help" moduleName="axp-supplier-dashboard-help" />
                  <ModuleRoute
                    path="account-settings(#:subTab)"
                    titleId="account-settings"
                    moduleName="axp-supplier-authenticated-account-settings"
                  />
                  <ModuleRoute
                    path="invoices"
                    moduleName="axp-supplier-invoice-management-invoices"
                  />
                  <ModuleRoute path="payments" moduleName="axp-supplier-payments" />
                  <ModuleRoute path="orders" moduleName="axp-invoice-management-orders" />
                  <ModuleRoute
                    path="goods-receipts"
                    moduleName="axp-invoice-management-goods-receipts"
                  />
                  <ModuleRoute
                    path="customers"
                    titleId="customers"
                    moduleName="axp-supplier-customer-management"
                  />
                  <ModuleRoute path="reporting" moduleName="axp-supplier-reporting" />
                </ModuleRoute>
              </ModuleRoute>
              <ModuleRoute path="link-account" moduleName="axp-supplier-link-account" />
            </ModuleRoute>
          </ModuleRoute>
        </ModuleRoute>
        <ModuleRoute title="UnAuthenticated Routes">
          <ModuleRoute component={Dls7Wrapper}>
            <ModuleRoute
              path="collect-virtual-payment"
              moduleName="axp-supplier-unauthenticated-virtual-payment-details"
            />
            <ModuleRoute path="supplier-dashboard">
              <ModuleRoute
                path="create-account"
                moduleName="axp-supplier-create-profile-virtual-payment"
              />
              <ModuleRoute
                path="create-account/result"
                moduleName="axp-supplier-create-profile-virtual-payment-response"
              />
              <ModuleRoute
                path="vcard-enrollment"
                moduleName="axp-supplier-digital-card-payment-onboarding"
              />
            </ModuleRoute>
          </ModuleRoute>
        </ModuleRoute>
      </ModuleRoute>
    </ModuleRoute>
    <ModuleRoute component={Dls7Wrapper}>
      <ModuleRoute
        titleId="invoice-management"
        path=":locale/business/one-ap-invoice-management/account"
      >
        <ModuleRoute
          moduleName="axp-page-wrapper"
          containerClassName=""
          headerProps={getInvoiceManagementHeaderProps(store.getState())}
        >
          <IndexRoute component={SystemError} />
          <ModuleRoute
            path="link-account"
            moduleName="axp-supplier-link-account"
            sourceSystem="invoiceManagement"
          />
          <ModuleRoute title="UnAuthenticated Routes">
            <ModuleRoute
              path="create-account"
              moduleName="axp-supplier-create-profile-virtual-payment"
              sourceSystem="invoiceManagement"
            />
            <ModuleRoute
              titleId="create-account"
              path="create-account/result"
              moduleName="axp-supplier-create-profile-virtual-payment-response"
              sourceSystem="invoiceManagement"
            />
          </ModuleRoute>
        </ModuleRoute>
      </ModuleRoute>
      <ModuleRoute
        moduleName="axp-page-wrapper"
        headerProps={getInvoiceManagementHeaderProps(store.getState())}
      >
        <ModuleRoute
          path="unauthorized-error"
          component={UnauthorizedError}
          httpStatus={401}
          sourceSystem="invoiceManagement"
        />
      </ModuleRoute>
    </ModuleRoute>
    <ModuleRoute component={Dls7Wrapper}>
      <ModuleRoute
        titleId="buyer-initiated-payments"
        path=":locale/business/payments/buyer-initiated-payments"
      >
        <ModuleRoute
          moduleName="axp-page-wrapper"
          containerClassName=""
          headerProps={getEnrollmentHeaderProps(store.getState())}
        >
          <ModuleRoute path="enrollment" moduleName="axp-supplier-enrollment" sourceSystem="BIP" />
        </ModuleRoute>
      </ModuleRoute>
    </ModuleRoute>
    <ModuleRoute component={Dls6Wrapper}>
      <ModuleRoute moduleName="axp-page-wrapper">
        <ModuleRoute
          path=":locale/business/suppliers/supplier-enrollment/:rsvpCode"
          titleId="simple-supplier-enrollment"
          moduleName="axp-supplier-onboarding"
        />
      </ModuleRoute>
    </ModuleRoute>
    <ModuleRoute component={Dls6Wrapper}>
      <ModuleRoute component={DemoWrapper}>
        <ModuleRoute path="demo/:moduleName" moduleName="axp-demo" />
      </ModuleRoute>
    </ModuleRoute>
    <ModuleRoute component={Dls7Wrapper}>
      <ModuleRoute component={DemoWrapper}>
        <ModuleRoute path="demo-dls7/:moduleName" moduleName="axp-demo" />
      </ModuleRoute>
    </ModuleRoute>
    <ModuleRoute component={Dls7Wrapper}>
      <ModuleRoute moduleName="axp-page-wrapper" headerProps={getHeaderProps(store.getState())}>
        <ModuleRoute path="(*/)system-error" component={SystemError} httpStatus={400} />
        <ModuleRoute path="(*/)unauthorized-error" component={UnauthorizedError} httpStatus={401} />
        <ModuleRoute path="(*/)access-error" component={AccessError} httpStatus={403} />
        <ModuleRoute path="(*/)no-products" component={NoProducts} httpStatus={403} />
        <ModuleRoute path="*" component={SystemError} httpStatus={404} />
      </ModuleRoute>
    </ModuleRoute>
  </ModuleRoute>,
];

export default childRoutes;
